import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card } from "@/components/ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import axios from "axios";
import { toast } from "sonner";
import { 
  ChevronDown, ChevronUp, Package, Truck, Users as UsersIcon, 
  Plus, Trash2, Coffee, Phone, User, FileText
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Default equipment items
const DEFAULT_EQUIPMENT = [
  { name: "Water container", quantity: 1, checked: false },
  { name: "Bibs", quantity: 1, color: "white", checked: false },
  { name: "Balls", quantity: 4, checked: false, notes: "ensure balls are pumped" },
  { name: "Medical box", quantity: 1, checked: false },
  { name: "Ice packs in green bucket", quantity: 1, checked: false },
  { name: "Deep heat cans", quantity: 2, checked: false, notes: "ensure cans are full" },
  { name: "Cones", quantity: 10, checked: false },
];

const BIB_COLORS = ["white", "red", "blue", "yellow", "green", "orange", "black"];

export default function LogisticsForm({ 
  teamId, 
  user, 
  onChange,
  initialData = null 
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [logistics, setLogistics] = useState({
    request_referee: false,
    referee_count: 3,
    request_transport: false,
    transport_notes: "",
    equipment_items: [],
    equipment_ready_time: "",
    request_refreshments: false,
    refreshments_details: "",
    contact_name: user?.name || "",
    contact_phone: user?.phone || "",
    special_instructions: ""
  });
  const [teamDefaults, setTeamDefaults] = useState(null);

  // Load team defaults when teamId changes
  useEffect(() => {
    if (teamId) {
      loadTeamDefaults();
    }
  }, [teamId]);

  // Notify parent of changes
  useEffect(() => {
    if (onChange) {
      // Only send logistics data if any request is active
      const hasRequest = logistics.request_referee || 
                         logistics.request_transport || 
                         logistics.request_refreshments ||
                         logistics.equipment_items.some(item => item.checked);
      
      if (hasRequest) {
        const filteredItems = logistics.equipment_items.filter(item => item.checked);
        onChange({
          ...logistics,
          equipment_items: filteredItems
        });
      } else {
        onChange(null);
      }
    }
  }, [logistics]);

  const loadTeamDefaults = async () => {
    try {
      const response = await axios.get(`${API}/teams/${teamId}/equipment-defaults`, { withCredentials: true });
      setTeamDefaults(response.data);
      
      // Initialize equipment from team defaults or use standard defaults
      const items = response.data?.items || DEFAULT_EQUIPMENT;
      setLogistics(prev => ({
        ...prev,
        equipment_items: items.map(item => ({ ...item, checked: false }))
      }));
    } catch (error) {
      console.error("Error loading team defaults:", error);
      // Use standard defaults
      setLogistics(prev => ({
        ...prev,
        equipment_items: DEFAULT_EQUIPMENT.map(item => ({ ...item, checked: false }))
      }));
    }
  };

  const updateField = (field, value) => {
    setLogistics(prev => ({ ...prev, [field]: value }));
  };

  const updateEquipmentItem = (index, field, value) => {
    setLogistics(prev => {
      const newItems = [...prev.equipment_items];
      newItems[index] = { ...newItems[index], [field]: value };
      return { ...prev, equipment_items: newItems };
    });
  };

  const addCustomItem = () => {
    setLogistics(prev => ({
      ...prev,
      equipment_items: [
        ...prev.equipment_items,
        { name: "", quantity: 1, checked: true, isCustom: true }
      ]
    }));
  };

  const removeItem = (index) => {
    setLogistics(prev => ({
      ...prev,
      equipment_items: prev.equipment_items.filter((_, i) => i !== index)
    }));
  };

  const saveAsTeamDefaults = async () => {
    try {
      const itemsToSave = logistics.equipment_items.map(({ checked, isCustom, ...item }) => item);
      await axios.put(`${API}/teams/${teamId}/equipment-defaults`, { items: itemsToSave }, { withCredentials: true });
      toast.success("Saved as team default equipment list");
    } catch (error) {
      toast.error("Failed to save defaults");
    }
  };

  const hasAnyRequest = logistics.request_referee || 
                        logistics.request_transport || 
                        logistics.request_refreshments ||
                        logistics.equipment_items.some(item => item.checked);

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger asChild>
        <Button 
          variant="outline" 
          className={`w-full justify-between ${hasAnyRequest ? 'border-purple-500/50 text-purple-400' : 'border-slate-600 text-slate-300'}`}
          type="button"
        >
          <div className="flex items-center gap-2">
            <Package className="w-4 h-4" />
            <span>Logistics Request (Staff)</span>
            {hasAnyRequest && (
              <span className="text-xs bg-purple-500/20 text-purple-400 px-2 py-0.5 rounded">Active</span>
            )}
          </div>
          {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </Button>
      </CollapsibleTrigger>
      
      <CollapsibleContent className="mt-4 space-y-6">
        {/* Referee Request */}
        <Card className="bg-slate-800/50 border-slate-700 p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Checkbox 
                id="request_referee"
                checked={logistics.request_referee}
                onCheckedChange={(checked) => updateField("request_referee", checked)}
              />
              <Label htmlFor="request_referee" className="text-white font-medium cursor-pointer">
                <UsersIcon className="w-4 h-4 inline mr-2 text-blue-400" />
                Request Referee Booking
              </Label>
            </div>
          </div>
          
          {logistics.request_referee && (
            <div className="pl-6 space-y-2">
              <div className="flex items-center gap-2">
                <Label className="text-slate-400 text-sm">Number of officials:</Label>
                <Select 
                  value={String(logistics.referee_count)} 
                  onValueChange={(v) => updateField("referee_count", parseInt(v))}
                >
                  <SelectTrigger className="w-20 bg-slate-900 border-slate-600">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-600">
                    <SelectItem value="1">1</SelectItem>
                    <SelectItem value="2">2</SelectItem>
                    <SelectItem value="3">3</SelectItem>
                    <SelectItem value="4">4</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
        </Card>

        {/* Transport Request */}
        <Card className="bg-slate-800/50 border-slate-700 p-4">
          <div className="flex items-center gap-2 mb-3">
            <Checkbox 
              id="request_transport"
              checked={logistics.request_transport}
              onCheckedChange={(checked) => updateField("request_transport", checked)}
            />
            <Label htmlFor="request_transport" className="text-white font-medium cursor-pointer">
              <Truck className="w-4 h-4 inline mr-2 text-purple-400" />
              Request Equipment Transport
            </Label>
          </div>
          
          {logistics.request_transport && (
            <div className="pl-6">
              <Textarea
                placeholder="Transport details (e.g., pickup location, special requirements)"
                value={logistics.transport_notes}
                onChange={(e) => updateField("transport_notes", e.target.value)}
                className="bg-slate-900 border-slate-600 text-white text-sm"
                rows={2}
              />
            </div>
          )}
        </Card>

        {/* Equipment Checklist */}
        <Card className="bg-slate-800/50 border-slate-700 p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Package className="w-4 h-4 text-green-400" />
              <span className="text-white font-medium">Equipment Needed</span>
            </div>
            <div className="flex items-center gap-2">
              <Input
                type="text"
                placeholder="Ready by (e.g., 1415hrs)"
                value={logistics.equipment_ready_time}
                onChange={(e) => updateField("equipment_ready_time", e.target.value)}
                className="w-40 bg-slate-900 border-slate-600 text-white text-sm"
              />
            </div>
          </div>
          
          <div className="space-y-2">
            {logistics.equipment_items.map((item, index) => (
              <div key={index} className="flex items-center gap-2 p-2 bg-slate-900/50 rounded">
                <Checkbox 
                  checked={item.checked}
                  onCheckedChange={(checked) => updateEquipmentItem(index, "checked", checked)}
                />
                
                {item.isCustom ? (
                  <Input
                    placeholder="Item name"
                    value={item.name}
                    onChange={(e) => updateEquipmentItem(index, "name", e.target.value)}
                    className="flex-1 bg-slate-800 border-slate-600 text-white text-sm h-8"
                  />
                ) : (
                  <span className={`flex-1 text-sm ${item.checked ? 'text-white' : 'text-slate-400'}`}>
                    {item.name}
                  </span>
                )}
                
                <Input
                  type="number"
                  min="1"
                  value={item.quantity}
                  onChange={(e) => updateEquipmentItem(index, "quantity", parseInt(e.target.value) || 1)}
                  className="w-16 bg-slate-800 border-slate-600 text-white text-sm h-8 text-center"
                />
                
                {item.name === "Bibs" && (
                  <Select 
                    value={item.color || "white"} 
                    onValueChange={(v) => updateEquipmentItem(index, "color", v)}
                  >
                    <SelectTrigger className="w-24 bg-slate-800 border-slate-600 h-8 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-600">
                      {BIB_COLORS.map(color => (
                        <SelectItem key={color} value={color}>{color}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                
                {(item.isCustom || item.notes !== undefined) && (
                  <Input
                    placeholder="Notes"
                    value={item.notes || ""}
                    onChange={(e) => updateEquipmentItem(index, "notes", e.target.value)}
                    className="w-32 bg-slate-800 border-slate-600 text-white text-sm h-8"
                  />
                )}
                
                {item.isCustom && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => removeItem(index)}
                    className="text-red-400 hover:text-red-300 hover:bg-red-500/10 h-8 w-8 p-0"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
            ))}
            
            <div className="flex gap-2 mt-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addCustomItem}
                className="border-slate-600 text-slate-300 hover:bg-slate-700"
              >
                <Plus className="w-4 h-4 mr-1" />
                Add Item
              </Button>
              
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={saveAsTeamDefaults}
                className="border-slate-600 text-slate-300 hover:bg-slate-700"
              >
                Save as Team Default
              </Button>
            </div>
          </div>
        </Card>

        {/* Refreshments */}
        <Card className="bg-slate-800/50 border-slate-700 p-4">
          <div className="flex items-center gap-2 mb-3">
            <Checkbox 
              id="request_refreshments"
              checked={logistics.request_refreshments}
              onCheckedChange={(checked) => updateField("request_refreshments", checked)}
            />
            <Label htmlFor="request_refreshments" className="text-white font-medium cursor-pointer">
              <Coffee className="w-4 h-4 inline mr-2 text-orange-400" />
              Request Refreshments
            </Label>
          </div>
          
          {logistics.request_refreshments && (
            <div className="pl-6">
              <Textarea
                placeholder="List refreshments needed (e.g., 24 isotonic, 36 Sapporo beer)"
                value={logistics.refreshments_details}
                onChange={(e) => updateField("refreshments_details", e.target.value)}
                className="bg-slate-900 border-slate-600 text-white text-sm"
                rows={2}
              />
            </div>
          )}
        </Card>

        {/* Contact Person */}
        <Card className="bg-slate-800/50 border-slate-700 p-4">
          <h4 className="text-white font-medium mb-3 flex items-center gap-2">
            <User className="w-4 h-4 text-slate-400" />
            Contact Person
          </h4>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-slate-400 text-sm">Name</Label>
              <Input
                value={logistics.contact_name}
                onChange={(e) => updateField("contact_name", e.target.value)}
                className="bg-slate-900 border-slate-600 text-white"
              />
            </div>
            <div>
              <Label className="text-slate-400 text-sm">Phone</Label>
              <Input
                value={logistics.contact_phone}
                onChange={(e) => updateField("contact_phone", e.target.value)}
                placeholder="+65 XXXX XXXX"
                className="bg-slate-900 border-slate-600 text-white"
              />
            </div>
          </div>
        </Card>

        {/* Special Instructions */}
        <Card className="bg-slate-800/50 border-slate-700 p-4">
          <h4 className="text-white font-medium mb-3 flex items-center gap-2">
            <FileText className="w-4 h-4 text-slate-400" />
            Special Instructions
          </h4>
          <Textarea
            placeholder="Any special instructions or notes for staff..."
            value={logistics.special_instructions}
            onChange={(e) => updateField("special_instructions", e.target.value)}
            className="bg-slate-900 border-slate-600 text-white text-sm"
            rows={3}
          />
        </Card>
      </CollapsibleContent>
    </Collapsible>
  );
}
